import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MspAnalyticsApi, { TicketAnalysisReportResponse } from "../apis/mspAnalyticsApi";
import { getApiErrorMessage } from "../utils/apiErrorMessage";

type SampleTicketRow = {
  id?: string | number;
  client_id?: string | number;
  customer_name?: string;
  ticket_id?: string | number;
  ticket_type?: string;
  alert_group?: string;
  problem_domain?: string;
  auto_investigation_score?: string | number;
  ticket_description?: string;
  ticket_description_full?: string;
  problem_summary?: string;
  problem_summary_full?: string;
  public_api_investigation_path?: string;
  public_api_investigation_path_full?: string;
};

export interface CachedTicketAnalysisPanelProps {
  dashboardCacheDay?: string | null;
  interval?: string | null;
}

type IssueDetailRow = {
  detail_name?: string;
  count?: number;
  share_pct?: number;
  sample_key?: string;
};

type IssueRow = {
  issue_group?: string;
  issue_name?: string;
  count?: number;
  share_pct?: number;
  sample_key?: string;
  details?: IssueDetailRow[];
};

type DomainRow = {
  problem_domain?: string;
  alert_count?: number;
  non_alert_count?: number;
  alert_share_pct?: number;
  avg_auto_investigation_score?: number;
  score_band?: string;
  alert_sample_key?: string;
  non_alert_sample_key?: string;
};

type CustomerSegment = {
  segment_key?: string;
  title?: string;
  summary?: ReportSummary;
};

type ReportSummary = {
  total?: number;
  alert_count?: number;
  non_alert_count?: number;
  avg_score?: number;
  date_range_label?: string;
  issue_rows?: IssueRow[];
  domain_rows?: DomainRow[];
  customer_segments?: CustomerSegment[];
};

const CachedTicketAnalysisPanel: React.FC<CachedTicketAnalysisPanelProps> = ({
  dashboardCacheDay = null,
  interval = null,
}) => {
  const [report, setReport] = useState<TicketAnalysisReportResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [sampleRows, setSampleRows] = useState<SampleTicketRow[]>([]);
  const [sampleTitle, setSampleTitle] = useState<string>("Sample Tickets");
  const [sampleOpen, setSampleOpen] = useState<boolean>(false);
  const [sampleLoading, setSampleLoading] = useState<boolean>(false);
  const [fullText, setFullText] = useState<{ title: string; text: string } | null>(null);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await MspAnalyticsApi.loadTicketAnalysisReport({
        cache_day: dashboardCacheDay,
        interval,
      });
      setReport(data);
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to load ticket analysis report."));
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [dashboardCacheDay, interval]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const data = event.data;
      if (!data || typeof data !== "object") return;
      if (data.type !== "ticket-analysis-sample-tickets") return;
      setSampleRows(Array.isArray(data.rows) ? data.rows : []);
      setSampleTitle(typeof data.title === "string" && data.title.trim() ? data.title : "Sample Tickets");
      setSampleOpen(true);
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const handleCopyDescription = useCallback(async (row: SampleTicketRow) => {
    const text = String(row.ticket_description_full || row.ticket_description || "");
    if (!text) return;
    await navigator.clipboard?.writeText(text);
  }, []);

  const summary = (report?.report_summary || null) as ReportSummary | null;

  const loadSamples = useCallback(async (sampleKey?: string, fallbackTitle = "Sample Tickets") => {
    if (!sampleKey || !report?.report_id) return;
    setSampleLoading(true);
    setSampleTitle(fallbackTitle);
    setSampleRows([]);
    setSampleOpen(true);
    try {
      const data = await MspAnalyticsApi.loadTicketAnalysisSamples({
        report_id: report.report_id,
        sample_key: sampleKey,
      });
      setSampleTitle(data.sample_title || fallbackTitle);
      setSampleRows(Array.isArray(data.sample_rows) ? data.sample_rows : []);
    } catch (err) {
      setSampleRows([]);
      setSampleTitle(getApiErrorMessage(err, "Failed to load sample tickets."));
    } finally {
      setSampleLoading(false);
    }
  }, [report?.report_id]);

  const formatNumber = (value?: number) => Number(value || 0).toLocaleString();

  const renderCountButton = (sampleKey: string | undefined, count: number | undefined, title: string) => {
    const n = Number(count || 0);
    if (!n || !sampleKey || !report?.report_id) return <>{formatNumber(n)}</>;
    return (
      <Button
        size="small"
        variant="text"
        sx={{ minWidth: 0, p: 0, fontWeight: 800 }}
        onClick={() => loadSamples(sampleKey, title)}
      >
        {formatNumber(n)}
      </Button>
    );
  };

  const renderPreviewCell = (
    row: SampleTicketRow,
    previewField: keyof SampleTicketRow,
    fullField: keyof SampleTicketRow,
    title: string,
    label: string,
    limit = 360,
    showCopy = false,
  ) => {
    const full = String(row[fullField] || row[previewField] || "");
    const preview = full.length > limit ? `${full.slice(0, limit).trimEnd()}...` : full;
    return (
      <Box sx={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>
        <Typography variant="caption" sx={{ display: "block", fontWeight: 800, color: "text.secondary", textTransform: "uppercase" }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.45 }}>
          {preview}
        </Typography>
        {full.length > preview.length && (
          <Button size="small" variant="text" sx={{ px: 0, minWidth: 0, mr: 1 }} onClick={() => setFullText({ title, text: full })}>
            View full
          </Button>
        )}
        {showCopy && (
          <Button size="small" variant="text" sx={{ px: 0, minWidth: 0 }} onClick={() => handleCopyDescription(row)}>
            Copy
          </Button>
        )}
      </Box>
    );
  };

  const orderedCustomerSegments = [...(summary?.customer_segments || [])].sort((a, b) => {
    const aLabel = String(a.title || a.segment_key || "").toLowerCase();
    const bLabel = String(b.title || b.segment_key || "").toLowerCase();
    const aIsOther = a.segment_key === "other-customers" || aLabel.startsWith("other ") || aLabel.startsWith("non-");
    const bIsOther = b.segment_key === "other-customers" || bLabel.startsWith("other ") || bLabel.startsWith("non-");
    if (aIsOther === bIsOther) return 0;
    return aIsOther ? -1 : 1;
  });

  const renderSummarySection = (currentSummary: ReportSummary, title?: string) => (
    <Box sx={{ mb: 4 }}>
      {title && (
        <Typography variant="h5" fontWeight={800} sx={{ mb: 2 }}>
          {title}
        </Typography>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 1.5, mb: 3 }}>
        {[
          ["Total Tickets", formatNumber(currentSummary.total)],
          ["Alert Tickets", formatNumber(currentSummary.alert_count)],
          ["Non-Alert Tickets", formatNumber(currentSummary.non_alert_count)],
          ["Avg API Investigation Score", Number(currentSummary.avg_score || 0).toFixed(1)],
        ].map(([label, value]) => (
          <Box key={label} sx={{ bgcolor: "#eff6ff", borderRadius: 2, p: 2 }}>
            <Typography sx={{ fontSize: "2rem", fontWeight: 800, color: "#1d4ed8", lineHeight: 1 }}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, mb: 3, overflow: "hidden" }}>
        <Box sx={{ p: 2.5, pb: 1 }}>
          <Typography variant="h6" fontWeight={800}>Issue Distribution</Typography>
          <Typography variant="body2" color="text.secondary">
            Alert tickets are grouped by inferred sub-issue type. Non-alert tickets are grouped by problem domain.
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800 }}>Group</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Issue</TableCell>
                <TableCell sx={{ fontWeight: 800 }} align="right">Tickets</TableCell>
                <TableCell sx={{ fontWeight: 800 }} align="right">Share Within Group</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(currentSummary.issue_rows || []).map((row, idx) => (
                <TableRow key={`${row.issue_group}-${row.issue_name}-${idx}`} hover>
                  <TableCell sx={{ verticalAlign: "top" }}>{row.issue_group}</TableCell>
                  <TableCell sx={{ verticalAlign: "top" }}>
                    <Typography variant="body2" fontWeight={800}>{row.issue_name}</Typography>
                    {!!row.details?.length && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                        {row.details.map((detail, detailIdx) => (
                          <Box
                            key={`${detail.detail_name}-${detailIdx}`}
                            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, bgcolor: "grey.100", border: "1px solid", borderColor: "divider", borderRadius: 999, px: 1, py: 0.5 }}
                          >
                            <Typography variant="caption">{detail.detail_name}:</Typography>
                            {renderCountButton(detail.sample_key, detail.count, `${detail.detail_name || "Issue"} Sample Tickets`)}
                            <Typography variant="caption" color="text.secondary">({Number(detail.share_pct || 0).toFixed(1)}%)</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ verticalAlign: "top" }}>
                    {renderCountButton(row.sample_key, row.count, `${row.issue_name || "Issue"} Sample Tickets`)}
                  </TableCell>
                  <TableCell align="right" sx={{ verticalAlign: "top" }}>{Number(row.share_pct || 0).toFixed(1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 2, mb: 3, overflow: "hidden" }}>
        <Box sx={{ p: 2.5, pb: 1 }}>
          <Typography variant="h6" fontWeight={800}>Alert vs Non-Alert by Problem Domain</Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800 }}>Problem Domain</TableCell>
                <TableCell sx={{ fontWeight: 800 }} align="right">Alerts</TableCell>
                <TableCell sx={{ fontWeight: 800 }} align="right">Non-Alerts</TableCell>
                <TableCell sx={{ fontWeight: 800 }} align="right">Alert Share</TableCell>
                <TableCell sx={{ fontWeight: 800 }} align="right">API Investigation Score</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Score Band</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(currentSummary.domain_rows || []).map((row, idx) => (
                <TableRow key={`${row.problem_domain}-${idx}`} hover>
                  <TableCell>{row.problem_domain}</TableCell>
                  <TableCell align="right">
                    {renderCountButton(row.alert_sample_key, row.alert_count, `${row.problem_domain || "Domain"} Alert Sample Tickets`)}
                  </TableCell>
                  <TableCell align="right">
                    {renderCountButton(row.non_alert_sample_key, row.non_alert_count, `${row.problem_domain || "Domain"} Non-Alert Sample Tickets`)}
                  </TableCell>
                  <TableCell align="right">{Number(row.alert_share_pct || 0).toFixed(1)}%</TableCell>
                  <TableCell align="right">{Number(row.avg_auto_investigation_score || 0).toFixed(1)}</TableCell>
                  <TableCell>{row.score_band}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ mb: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading cached ticket analysis report...
            </Typography>
          </Box>
        )}

        {!loading && !error && !summary && (
          <Alert severity="info">
            No cached ticket analysis report is available yet for this MSP. Generate one with the analyzer's
            dashboard-cache option, then refresh this tab.
          </Alert>
        )}

        {!loading && !error && summary && (
          <Box>
            <Typography variant="overline">Saved AI analysis report · batch generated</Typography>
            <Typography variant="h4" fontWeight={800} sx={{ mb: 1 }}>
              Alert vs Non-Alert Problem Domain Analysis
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Date range {summary.date_range_label || report?.report_metadata?.date_range_label || "unavailable"} · generated{" "}
              {report?.report_metadata?.generated_at || report?.dashboard_cache?.updated_at || "unknown"}
            </Typography>

            {orderedCustomerSegments.length
              ? orderedCustomerSegments.map((segment) =>
                  renderSummarySection(segment.summary || {}, segment.title || "Customer Segment"),
                )
              : renderSummarySection(summary)}
          </Box>
        )}
      </Box>

      <Dialog
        open={sampleOpen}
        onClose={() => {
          setSampleOpen(false);
          setSampleRows([]);
        }}
        maxWidth="xl"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            width: "min(1680px, calc(100vw - 48px))",
            height: "min(920px, calc(100vh - 48px))",
            maxWidth: "none",
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
          {sampleTitle}
          <IconButton
            aria-label="Close sample tickets"
            onClick={() => {
              setSampleOpen(false);
              setSampleRows([]);
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {sampleLoading ? (
            <Box sx={{ py: 8, textAlign: "center" }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading sample tickets...</Typography>
            </Box>
          ) : sampleRows.length === 0 ? (
            <Alert severity="info" sx={{ m: 2 }}>No sample tickets available.</Alert>
          ) : (
          <TableContainer sx={{ maxHeight: "100%" }}>
            <Table stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800, width: 115 }}>PSA Ticket</TableCell>
                  <TableCell sx={{ fontWeight: 800, width: 130 }}>Ticket Type</TableCell>
                  <TableCell sx={{ fontWeight: 800, width: 150 }}>Classification</TableCell>
                  <TableCell sx={{ fontWeight: 800, width: 75 }} align="right">Score</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Ticket Description</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Problem</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>What Theo can Do</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sampleRows.map((row, idx) => (
                  <TableRow key={`${row.id || row.ticket_id || idx}-${idx}`} hover>
                    <TableCell>
                      <Link href={`https://runtheo.app/supervisor/tickets/${encodeURIComponent(String(row.ticket_id || ""))}`} target="_blank" rel="noopener noreferrer" fontWeight={700}>
                        {row.ticket_id}
                      </Link>
                    </TableCell>
                    <TableCell>{row.ticket_type}</TableCell>
                    <TableCell>
                      {row.alert_group}
                      <Typography variant="caption" color="text.secondary" display="block">
                        {row.problem_domain}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{row.auto_investigation_score}</TableCell>
                    <TableCell sx={{ verticalAlign: "top" }}>{renderPreviewCell(row, "ticket_description", "ticket_description_full", "Full Ticket Description", "Description", 360, true)}</TableCell>
                    <TableCell sx={{ verticalAlign: "top" }}>{renderPreviewCell(row, "problem_summary", "problem_summary_full", "Full Problem Summary", "Problem", 260)}</TableCell>
                    <TableCell sx={{ verticalAlign: "top" }}>{renderPreviewCell(row, "public_api_investigation_path", "public_api_investigation_path_full", "What Theo can Do", "What Theo can Do", 260)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(fullText)} onClose={() => setFullText(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
          {fullText?.title || "Full Text"}
          <IconButton aria-label="Close full text" onClick={() => setFullText(null)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{fullText?.text}</Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CachedTicketAnalysisPanel;
